import { ReactNode } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import { FilterBar, useFilterBarCollapse } from "@impero-com/ui";
import { Checkbox } from "antd";

import { FilterRow } from "components/filters/FilterRow";
import { localizeFilterType } from "components/filters/filterType";
import { TimeFilter } from "components/filters/TimeFilter";
import { TagCategory } from "components/filters/types";
import { useFilterRows } from "components/filters/useFilterRows";
import { useTagsSuspenseQuery } from "org/tags/queries/useTagsQuery";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";

import { CONTROL_HEALTH_PENDO, FILTER_PENDO } from "../pendo";

import * as theme from "./ControlHealthFilterPanel.css";
import { useControlHealthFilters } from "./ControlHealthFiltersProvider";
import {
    clearedFilterValue,
    countActiveFilters,
    deriveFilterTypes,
    getEnabledFilterTypes,
    isDateRangePresetActive,
} from "./filterUtils";

/** The collapsible filter panel at the top of the Control Health page. */
export function ControlHealthFilterPanel({
    headerActions,
}: {
    headerActions?: ReactNode;
}) {
    const { t } = useTranslation();
    const { filters, setFilters, clearFilters } = useControlHealthFilters();

    const controlEntitiesEnabled = useFeatureFlag("controlEntities");
    const enabledFilterTypes = getEnabledFilterTypes(controlEntitiesEnabled);
    const tagCategories = useTagCategories();

    const [collapsed, setCollapsed] = useFilterBarCollapse("controlHealth");

    const { rows, addRow, removeRow, replaceRow, clearRows } = useFilterRows(
        () => deriveFilterTypes(filters),
        (filterType) =>
            setFilters({ ...filters, ...clearedFilterValue(filterType) })
    );

    const clearAll = () => {
        clearFilters();
        clearRows();
    };

    const addFilterItems = enabledFilterTypes
        .filter((filterType) => !rows.includes(filterType))
        .map((filterType) => ({
            label: localizeFilterType(filterType, t),
            key: filterType,
            "data-pendo": FILTER_PENDO[filterType].add,
            onClick: () => addRow(filterType),
        }));

    return (
        <FilterBar
            headerActions={headerActions}
            pendoIds={{
                toggle: CONTROL_HEALTH_PENDO.filtersToggle,
                add: CONTROL_HEALTH_PENDO.addFilter,
                clearAll: CONTROL_HEALTH_PENDO.clearAll,
            }}
            collapsed={collapsed}
            onCollapsedChange={setCollapsed}
            addLabel={t("FilterBar_AddFilter")}
            hideFiltersLabel={t("FilterBar_HideFilters")}
            clearAllLabel={t("FilterBar_ClearAll")}
            getFilterCountLabel={(count) =>
                t("FilterBar_FiltersApplied", { count })
            }
            addFilterItems={addFilterItems}
            filterCount={countActiveFilters(filters)}
            rowCountLabel={undefined}
            onClearFilters={clearAll}
            aboveFilters={
                <div className={theme.dateRangeRow}>
                    <TimeFilter
                        data-pendo={CONTROL_HEALTH_PENDO.period}
                        value={filters.dateRangePreset}
                        onChange={(dateRangePreset) =>
                            setFilters({ ...filters, dateRangePreset })
                        }
                    />
                </div>
            }
            belowFilters={
                <div className={theme.checkboxRow}>
                    <Checkbox
                        checked={filters.showInactivePrograms}
                        data-pendo={CONTROL_HEALTH_PENDO.showInactivePrograms}
                        onChange={(e) =>
                            setFilters({
                                ...filters,
                                showInactivePrograms: e.target.checked,
                            })
                        }
                    >
                        {t(
                            "Dashboard_ContentFilter_OptionShowInactivePrograms"
                        )}
                    </Checkbox>
                    <Checkbox
                        checked={filters.showArchivedActivities}
                        data-pendo={CONTROL_HEALTH_PENDO.showArchivedActivities}
                        onChange={(e) =>
                            setFilters({
                                ...filters,
                                showArchivedActivities: e.target.checked,
                            })
                        }
                    >
                        {t(
                            "Dashboard_ContentFilter_OptionShowArchivedActivities"
                        )}
                    </Checkbox>
                </div>
            }
            extraFilterCount={
                Number(isDateRangePresetActive(filters)) +
                Number(filters.showInactivePrograms) +
                Number(filters.showArchivedActivities)
            }
        >
            {rows.map((filterType, index) => (
                <FilterRow
                    data-pendo={FILTER_PENDO[filterType].row}
                    removeButtonPendoId={CONTROL_HEALTH_PENDO.removeFilter}
                    key={`${filterType}-${index}`}
                    filterType={filterType}
                    index={index}
                    enabledFilterTypes={enabledFilterTypes}
                    selectedFilters={rows}
                    onFilterRemove={removeRow}
                    onFilterTypeChange={replaceRow}
                    tagCategories={tagCategories}
                    selectedTags={filters.tags}
                    onTagsChange={(tags) => setFilters({ ...filters, tags })}
                    selectedControlPrograms={filters.controlPrograms}
                    onControlProgramsChange={(controlPrograms) =>
                        setFilters({ ...filters, controlPrograms })
                    }
                    selectedEntities={filters.entities}
                    onEntitiesChange={(entities) =>
                        setFilters({ ...filters, entities })
                    }
                />
            ))}
        </FilterBar>
    );
}

/**
 * The org's tag catalogue, shaped for the tags filter. The options come from the
 * catalogue rather than from whatever tags the loaded activities happen to use.
 */
function useTagCategories(): TagCategory[] {
    const { data } = useTagsSuspenseQuery();

    return data.tagCategories
        .filter((category) => category.active)
        .map((category) => ({
            name: category.name,
            tags: data.tags
                .filter(
                    (tag) => tag.active && tag.tagCategoryId === category.id
                )
                .map((tag) => tag.name),
        }));
}
